import type { Practitioner } from '@/mocks/practitioners';

interface BookingSidebarProps {
  practitioner: Practitioner;
  onBook: () => void;
}

export default function BookingSidebar({ practitioner: p, onBook }: BookingSidebarProps) {
  const waLink = p.whatsapp
    ? `https://wa.me/${p.whatsapp}?text=${encodeURIComponent(
        `Bonjour ${p.name}, je souhaite obtenir des informations et prendre un rendez-vous via MedicaClic.`,
      )}`
    : '';

  return (
    <aside className="lg:sticky lg:top-24">
      <div className="bg-background-50 border border-background-200 rounded-2xl p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
            Tarif de consultation
          </span>
          {p.isAvailableNow && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 text-primary-800 text-[0.65rem] font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-soft-pulse" />
              Disponible
            </span>
          )}
        </div>

        <p className="font-heading text-2xl font-semibold text-foreground-950 mb-1">
          {p.price > 0 ? (
            <>
              dès <span className="text-primary-600">{p.price} MAD</span>
            </>
          ) : (
            p.category === 'Pharmacies' ? 'Tarifs en pharmacie' : 'Tarif sur demande'
          )}
        </p>
        {p.nextSlot && (
          <p className="text-sm text-foreground-600 flex items-center gap-1.5 mb-6">
            <i className="ri-calendar-line text-foreground-400 w-4 h-4 flex items-center justify-center" />
            {p.nextSlot}
          </p>
        )}

        <button
          onClick={onBook}
          className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-calendar-check-line w-4 h-4 flex items-center justify-center" />
          Prendre rendez-vous
        </button>

        {p.phone && (
          <a
            href={`tel:${p.phone.replace(/\s+/g, '')}`}
            className="mt-3 w-full flex items-center justify-center gap-2 px-5 py-3 rounded-md border border-background-300 hover:border-primary-400 text-foreground-800 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-line w-4 h-4 flex items-center justify-center" />
            Appeler le cabinet · {p.phone}
          </a>
        )}

        {waLink && (
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 w-full flex items-center justify-center gap-2 px-5 py-3 rounded-md border border-background-300 hover:border-primary-400 text-foreground-800 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center" />
            Contacter sur WhatsApp
          </a>
        )}

        <div className="mt-6 pt-5 border-t border-background-200 space-y-3">
          <p className="text-xs text-foreground-600 flex items-start gap-2 leading-relaxed">
            <i className="ri-time-line text-primary-500 w-4 h-4 flex items-center justify-center mt-0.5" />
            Créneaux affichés en temps réel, mis à jour automatiquement.
          </p>
          <p className="text-xs text-foreground-600 flex items-start gap-2 leading-relaxed">
            <i className="ri-shield-check-line text-primary-500 w-4 h-4 flex items-center justify-center mt-0.5" />
            Réservation sans frais, confirmation par le praticien.
          </p>
          <p className="text-xs text-foreground-600 flex items-start gap-2 leading-relaxed">
            <i className="ri-lock-2-line text-primary-500 w-4 h-4 flex items-center justify-center mt-0.5" />
            Données protégées — conforme Loi 09-08 (CNDP).
          </p>
        </div>
      </div>
    </aside>
  );
}